import React, { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import TranslationNS from '@/utils/Constants/TranslationNS'
import { InviteAccessMenuRow } from '@/components/Organizer/InviteAccess'
import { menuGroupAnchor } from '@/components/Organizer/Tours/tours.utils'
import { Chevron, MenuIcon } from './orgMenuIcons'

const hrefOf = (base, to) => (to ? `${base}/${to}` : base)

function Entry({ base, entry, active, competition, setCompetition, onNavigate }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    if (entry.invite) {
        return (
            <li>
                <InviteAccessMenuRow
                    type={entry.invite}
                    competition={competition}
                    setCompetition={setCompetition}
                    className="osd-item"
                />
            </li>
        )
    }
    return (
        <li>
            <Link
                to={hrefOf(base, entry.to)}
                className={`osd-item${active ? ' on' : ''}`}
                aria-current={active ? 'page' : undefined}
                onClick={onNavigate}
            >
                {t(entry.title)}
            </Link>
        </li>
    )
}

/** Titled, collapsible sub-block of the Contracts group (mock `.osd-subgrp`): open by default, independent. */
function Section({ section, ...rest }) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const [open, setOpen] = useState(true)
    const id = `osdSec-${section.key}`
    return (
        <li className="osd-subgrp">
            <button
                type="button"
                className="osd-h osd-hbtn"
                aria-expanded={open}
                aria-controls={id}
                onClick={() => setOpen((v) => !v)}
            >
                <span>{t(section.title)}</span>
                <Chevron small />
            </button>
            <ul className="osd-sub2" id={id} hidden={!open}>
                {section.childs.map((child) => (
                    <Entry key={child.key} entry={child} active={rest.activeEntry === child} {...rest} />
                ))}
            </ul>
        </li>
    )
}

/**
 * The groups of the side panel (mock `.osd-list`): one accordion open at a time; the group holding the
 * active entry unfolds on its own when the panel is unfolded (hover / pinned / drawer) and is only
 * marked in rail mode. `base` = the event root (with the /sporthub prefix when present).
 * `forcedOpenKey` (a guided tour step): that group opens for as long as the key is set, then the
 * accordion goes back to what it showed before. Every group `li` (row + its opened entries) carries a
 * `data-tour` anchor (`menuGroupAnchor`, Tours) so the dashboard tour can spotlight it whole.
 */
export default function OrgMenuList({
    groups,
    base,
    activeEntry,
    activeGroup,
    unfolded,
    forcedOpenKey = null,
    competition,
    setCompetition,
    onNavigate,
}) {
    const { t } = useTranslation(TranslationNS.EVENT_PAGE)
    const [openKey, setOpenKey] = useState(activeGroup?.key ?? null)
    const openKeyRef = useRef(openKey)
    openKeyRef.current = openKey
    const stash = useRef(null)

    useEffect(() => {
        if (unfolded && activeGroup) setOpenKey(activeGroup.key)
    }, [unfolded, activeGroup])

    useEffect(() => {
        if (forcedOpenKey) {
            if (!stash.current) stash.current = { key: openKeyRef.current }
            setOpenKey(forcedOpenKey)
        } else if (stash.current) {
            setOpenKey(stash.current.key)
            stash.current = null
        }
    }, [forcedOpenKey])

    const entryProps = { base, competition, setCompetition, onNavigate, activeEntry }

    return (
        <ul className="osd-list" data-tour="menu-groups">
            {groups.map((group) => {
                const icon = <MenuIcon name={group.icon} />
                if (group.to !== undefined) {
                    const active = activeEntry === group
                    return (
                        <li key={group.key} data-tour={menuGroupAnchor(group.key)}>
                            <Link
                                to={hrefOf(base, group.to)}
                                className={`osd-item${active ? ' on' : ''}`}
                                aria-current={active ? 'page' : undefined}
                                onClick={onNavigate}
                            >
                                {icon}
                                <span className="osd-lbl">{t(group.title)}</span>
                            </Link>
                        </li>
                    )
                }
                const open = openKey === group.key
                const id = `osdGrp-${group.key.replace(/[^a-z0-9]/gi, '')}`
                return (
                    <li key={group.key} className="osd-grp" data-tour={menuGroupAnchor(group.key)}>
                        <button
                            type="button"
                            className={`osd-item${activeGroup === group ? ' on' : ''}`}
                            aria-expanded={open}
                            aria-controls={id}
                            onClick={() => setOpenKey(open ? null : group.key)}
                        >
                            {icon}
                            <span className="osd-lbl">{t(group.title)}</span>
                            <Chevron />
                        </button>
                        <ul className="osd-sub" id={id} hidden={!open}>
                            {group.sections
                                ? group.sections.map((section) => (
                                      <Section key={section.key} section={section} {...entryProps} />
                                  ))
                                : group.childs.map((child) => (
                                      <Entry
                                          key={child.key}
                                          entry={child}
                                          active={activeEntry === child}
                                          {...entryProps}
                                      />
                                  ))}
                        </ul>
                    </li>
                )
            })}
        </ul>
    )
}
